import { useEffect, useState } from 'react';

/**
 * Returns `true` when the tracked element's bottom has scrolled above the
 * cover element's bottom — i.e. the tracked element is fully covered (or
 * above) the cover. Reads live bounding rects on scroll/resize, so it stays
 * accurate even as the cover's position shifts (e.g. the main site header
 * sliding in above a sticky sub-header).
 *
 * Both `ref.current` and `coverRef.current` are read **inside** the check
 * function rather than captured at effect setup. This matters when the
 * tracked element mounts asynchronously (e.g. via a portal that attaches its
 * target on a follow-up render): the effect deps are ref objects with stable
 * identity, so the effect only runs once. Reading `ref.current` dynamically
 * means a late-mounted node is picked up on the next scroll/resize tick,
 * rather than leaving the hook with `isHidden` permanently stuck on whatever
 * value it last computed.
 */
export default function useIsHiddenBehind(
  ref: React.RefObject<HTMLElement> | undefined,
  coverRef: React.RefObject<HTMLElement> | undefined
): boolean {
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let rafId: number | null = null;
    const check = () => {
      rafId = null;
      const el = ref?.current;
      if (!el) {
        // Element hasn't mounted yet (or has unmounted). Treat as not hidden
        // so the sticky cover stays collapsed until we have a real position
        // to compare against.
        setIsHidden(false);
        return;
      }
      const coverBottom =
        coverRef?.current?.getBoundingClientRect().bottom ?? 0;
      setIsHidden(el.getBoundingClientRect().bottom <= coverBottom);
    };
    const schedule = () => {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(check);
      }
    };

    check();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [ref, coverRef]);

  return isHidden;
}
